// Words for procurement states and match differences (DESIGN.md §7 status tags, §9 writing).
// States are words plus a tag shape; no colours.
import type { InvoiceMatchView, PurchaseOrderView } from '@wodi/shared';
import type { TagState } from '@wodi/ui';

import { formatCents } from '../stock/format.ts';

export const ORDER_STATUS_LABELS: Record<PurchaseOrderView['status'], string> = {
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  closed: 'Closed short',
};

export const RECEIPT_LABELS: Record<PurchaseOrderView['receiptStatus'], string> = {
  not_received: 'Nothing received',
  partly_received: 'Part received',
  received: 'Received',
  over_received: 'More received than ordered',
};

/** One phrase for an LPO: "Approved, part received". */
export function orderStateWords(
  status: PurchaseOrderView['status'],
  receipt: PurchaseOrderView['receiptStatus'],
): string {
  if (status === 'approved' || status === 'closed')
    return `${ORDER_STATUS_LABELS[status]}, ${RECEIPT_LABELS[receipt].toLowerCase()}`;
  return ORDER_STATUS_LABELS[status];
}

export const DOCUMENT_TAGS: Record<string, TagState> = {
  awaiting_approval: 'pending',
  requested: 'pending',
  open: 'pending',
  approved: 'done',
  issued: 'done',
  partly_issued: 'active',
  rejected: 'failed',
  cancelled: 'neutral',
  closed: 'neutral',
};

export const DOCUMENT_LABELS: Record<string, string> = {
  awaiting_approval: 'Awaiting approval',
  requested: 'Waiting for approval',
  open: 'Waiting to be issued',
  partly_issued: 'Part issued',
  issued: 'Issued',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  closed: 'Closed short',
};

export const documentLabel = (status: string) => DOCUMENT_LABELS[status] ?? status;
export const documentTag = (status: string): TagState => DOCUMENT_TAGS[status] ?? 'neutral';

type Flag = InvoiceMatchView['lines'][number]['flags'][number];

export const FLAG_LABELS: Record<Flag, string> = {
  price_above_order: 'Price above LPO',
  price_below_order: 'Price below LPO',
  quantity_above_received: 'More invoiced than received',
  quantity_above_ordered: 'More invoiced than ordered',
};

/** A match line's differences as sentences, or "Matches" when there are none. */
export function matchLineWords(line: InvoiceMatchView['lines'][number]): string[] {
  if (line.flags.length === 0) return ['Matches'];
  return line.flags.map((flag) => {
    switch (flag) {
      case 'price_above_order':
      case 'price_below_order':
        return `${FLAG_LABELS[flag]}: ${formatCents(line.packPrice.cents)} per ${line.packUnitDisplay}; LPO ${formatCents(line.orderPackPrice.cents)}`;
      case 'quantity_above_received':
        return `${FLAG_LABELS[flag]}: ${String(line.invoiced + line.invoicedElsewhere)} invoiced, ${String(line.received)} received (${line.baseUnitDisplay})`;
      case 'quantity_above_ordered':
        return `${FLAG_LABELS[flag]}: ${String(line.invoiced + line.invoicedElsewhere)} invoiced, ${String(line.ordered)} ordered (${line.baseUnitDisplay})`;
    }
  });
}

/**
 * Whether "Approve for payment" may be pressed: a clean match needs no reason; any
 * difference needs a reason of at least five characters (plan §11, default 7).
 */
export function canApproveInvoice(match: InvoiceMatchView, reason: string): boolean {
  return match.matched || reason.trim().length >= 5;
}

/** The first and last calendar day of the month containing `today` (YYYY-MM-DD). */
export function monthOf(today: string): { from: string; to: string } {
  const [year = 1970, month = 1] = today.split('-').map(Number);
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const mm = String(month).padStart(2, '0');
  return { from: `${String(year)}-${mm}-01`, to: `${String(year)}-${mm}-${String(last)}` };
}

/** Today in East Africa Time, as YYYY-MM-DD. */
export function todayEat(now = new Date()): string {
  return new Date(now.getTime() + 3 * 3_600_000).toISOString().slice(0, 10);
}

/** Cents as text a person can edit: 120000 → "1200.00". */
export function centsToText(cents: string): string {
  const value = BigInt(cents);
  return `${String(value / 100n)}.${String(value % 100n).padStart(2, '0')}`;
}
